import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useEventSeo } from "@/lib/useEventSeo";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Check, X, MapPin, CalendarDays, Clock, Users, PartyPopper,
  CalendarPlus, Sparkles, ArrowRight, ArrowLeft, Heart,
  UploadCloud, FileCheck2, Loader2, Images, Film, CalendarClock,
  Navigation, UserCheck, Printer, Download,
} from "lucide-react";
import { eventsApi, fileUrl } from "@/lib/api";
import { formatEventDate, formatTime, formatDateRange, formatTimeRange, countdown, buildIcs, buildScheduleIcs, printItinerary } from "@/lib/helpers";
import Lightbox from "@/components/Lightbox";

const inputCls = "w-full rounded-xl border border-input bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-gold focus:ring-2 focus:ring-gold/20";

export default function PublicRSVP() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [cd, setCd] = useState(null);
  const [attendees, setAttendees] = useState({ enabled: true, total: 0, names: [] });
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const [lb, setLb] = useState({ items: null, index: null });

  const navigate = useNavigate();
  const eventId = event?.id;

  const loadAttendees = useCallback(() => {
    if (eventId) eventsApi.attendees(eventId).then(setAttendees).catch(() => {});
  }, [eventId]);

  useEffect(() => {
    if (event && (event.slug === id || event.id === id)) return;
    eventsApi.get(id).then((e) => {
      setEvent(e);
      setCd(countdown(e.date));
      if (e.slug && e.slug !== id) navigate(`/rsvp/${e.slug}${window.location.search}${window.location.hash}`, { replace: true });
    }).catch(() => setNotFound(true));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAttendees(); }, [loadAttendees]);
  useEventSeo(event);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-center">
        <PartyPopper className="h-12 w-12 text-gold" />
        <h1 className="mt-4 font-serif text-3xl font-semibold">Invitation not found</h1>
        <p className="mt-2 text-muted-foreground">This link may have expired or been removed.</p>
        <Link to="/" className="mt-6 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">Back home</Link>
      </div>
    );
  }
  if (!event) {
    return <div className="flex min-h-screen items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" /></div>;
  }

  const photos = (event.gallery || []).filter((m) => m.type !== "video");
  const videos = (event.gallery || []).filter((m) => m.type === "video");
  const schedule = event.schedule || [];
  const scheduleGroups = (() => {
    const groups = [];
    const byKey = {};
    schedule.forEach((it) => {
      const key = it.day || "";
      if (!byKey[key]) {
        byKey[key] = { label: it.day ? formatEventDate(it.day) : "Schedule", items: [] };
        groups.push(byKey[key]);
      }
      byKey[key].items.push(it);
    });
    return groups;
  })();
  const mapQuery = encodeURIComponent([event.venue_name, event.address].filter(Boolean).join(", "));
  const hasMap = Boolean(event.venue_name || event.address);

  const openLb = (items, index) => setLb({ items, index });

  const navItems = [
    { id: "about", label: "About", show: !!event.description },
    { id: "schedule", label: "Schedule", show: schedule.length > 0 },
    { id: "gallery", label: "Gallery", show: photos.length + videos.length > 0 },
    { id: "location", label: "Location", show: hasMap },
    { id: "attending", label: "Attending", show: attendees.enabled },
  ].filter((n) => n.show);

  const scrollTo = (sid) => {
    const el = document.getElementById(sid);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <div className="relative h-[60vh] min-h-[420px] bg-cover bg-center" style={{ backgroundImage: `url(${event.cover_image})` }}>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-background" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-3xl px-5 pb-12 text-center text-white">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="eyebrow text-gold">{event.event_type}</p>
            <h1 className="mt-3 font-serif text-4xl font-semibold tracking-tight sm:text-6xl">{event.title}</h1>
            {event.host_name && <p className="mt-3 text-white/80">Hosted by {event.host_name}</p>}
            <p className="mt-2 text-white/90">{formatDateRange(event.date, event.end_date)}{formatTimeRange(event.time, event.end_time) && ` • ${formatTimeRange(event.time, event.end_time)}`}</p>
            {cd && (
              <div className="mt-5 inline-flex items-center gap-4 rounded-full bg-white/10 px-5 py-2 text-sm backdrop-blur">
                <span><b className="font-serif text-lg">{cd.days}</b> days</span>
                <span className="opacity-40">•</span>
                <span><b className="font-serif text-lg">{cd.hours}</b> hrs</span>
                <span className="opacity-40">•</span>
                <span><b className="font-serif text-lg">{cd.mins}</b> min</span>
              </div>
            )}
            <div className="mt-7">
              <button
                data-testid="hero-rsvp-btn"
                onClick={() => setRsvpOpen(true)}
                className="group inline-flex items-center gap-2 rounded-full bg-gold px-10 py-4 text-base font-semibold text-primary transition-all hover:shadow-2xl hover:shadow-gold/30"
              >
                RSVP Now <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Sticky section nav */}
      {navItems.length > 0 && (
        <div className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-xl">
          <div className="mx-auto flex max-w-4xl items-center gap-1 overflow-x-auto px-5 py-3">
            {navItems.map((n) => (
              <button key={n.id} onClick={() => scrollTo(n.id)} data-testid={`nav-${n.id}`} className="whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                {n.label}
              </button>
            ))}
            <button onClick={() => setRsvpOpen(true)} className="ml-auto whitespace-nowrap rounded-full bg-primary px-5 py-1.5 text-sm font-semibold text-primary-foreground">RSVP</button>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-4xl px-5 py-12 sm:px-8">
        {/* Detail cards */}
        {(() => {
          const timeRange = formatTimeRange(event.time, event.end_time);
          const cards = [
            { icon: CalendarDays, label: "When", value: formatDateRange(event.date, event.end_date) },
            ...(timeRange ? [{ icon: Clock, label: "Time", value: timeRange }] : []),
            { icon: MapPin, label: "Where", value: event.venue_name || "TBA" },
          ];
          return (
            <div className={`grid gap-3 ${cards.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
              {cards.map((d) => (
                <div key={d.label} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                  <d.icon className="h-5 w-5 text-gold" />
                  <p className="eyebrow mt-2 text-muted-foreground">{d.label}</p>
                  <p className="mt-1 text-sm font-medium leading-snug">{d.value}</p>
                </div>
              ))}
            </div>
          );
        })()}

        {/* About */}
        {event.description && (
          <Section id="about" title="About the event">
            <p className="whitespace-pre-line text-center text-lg leading-relaxed text-muted-foreground">{event.description}</p>
          </Section>
        )}

        {/* Schedule */}
        {schedule.length > 0 && (
          <Section id="schedule" title="Schedule & Itinerary" icon={CalendarClock}>
            <div className="mx-auto mb-8 max-w-2xl rounded-2xl border border-border bg-card p-6 text-center">
              <p className="font-serif text-2xl font-semibold">{event.title}</p>
              <div className="mt-2 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-gold" /> {formatDateRange(event.date, event.end_date)}</span>
                {event.venue_name && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-gold" /> {event.venue_name}</span>}
              </div>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <a href={buildScheduleIcs(event, schedule)} download={`${event.title}-itinerary.ics`} data-testid="schedule-calendar-btn" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90">
                  <Download className="h-4 w-4" /> Add to calendar
                </a>
                <button onClick={() => printItinerary(event, scheduleGroups)} data-testid="schedule-print-btn" className="inline-flex items-center gap-1.5 rounded-full border border-border px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-secondary">
                  <Printer className="h-4 w-4" /> Print itinerary
                </button>
              </div>
            </div>
            <div className="mx-auto max-w-2xl space-y-10">
              {scheduleGroups.map((grp, gi) => (
                <div key={gi}>
                  {scheduleGroups.length > 1 && (
                    <h3 className="mb-5 text-center font-serif text-xl font-semibold text-gold">{grp.label}</h3>
                  )}
                  <div className="relative">
                    <div className="absolute bottom-2 left-[72px] top-2 w-px bg-border sm:left-24" />
                    <div className="space-y-6">
                      {grp.items.map((s, i) => (
                        <motion.div key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative flex gap-5" data-testid={`schedule-item-${gi}-${i}`}>
                          <div className="w-16 shrink-0 pt-1 text-right font-serif text-sm font-semibold text-gold sm:w-20">{s.time || "—"}</div>
                          <span className="relative z-10 mt-1.5 h-3 w-3 shrink-0 rounded-full bg-gold ring-4 ring-background" />
                          <div className="flex-1 pb-1">
                            <h4 className="font-semibold">{s.title}</h4>
                            {s.description && <p className="mt-1 text-sm text-muted-foreground">{s.description}</p>}
                            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                              {s.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-gold" /> {s.location}</span>}
                              {s.notes && <span className="inline-flex items-center gap-1 italic">{s.notes}</span>}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Photo gallery */}
        {photos.length > 0 && (
          <Section id="gallery" title="Photo gallery" icon={Images}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {photos.map((m, i) => (
                <button key={m.id} data-testid={`photo-${m.id}`} onClick={() => openLb(photos, i)} className="group overflow-hidden rounded-2xl border border-border bg-secondary">
                  <img src={fileUrl(m.url)} alt={m.filename || "Photo"} className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </button>
              ))}
            </div>
          </Section>
        )}

        {/* Video gallery */}
        {videos.length > 0 && (
          <Section id={photos.length ? "videos" : "gallery"} title="Video gallery" icon={Film}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {videos.map((m, i) => (
                <button key={m.id} data-testid={`video-${m.id}`} onClick={() => openLb(videos, i)} className="group relative overflow-hidden rounded-2xl border border-border bg-black">
                  <video src={fileUrl(m.url)} className="aspect-video w-full object-cover opacity-90" muted />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/20 backdrop-blur transition-transform group-hover:scale-110">
                      <Film className="h-6 w-6 text-white" />
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </Section>
        )}

        {/* Location / Map */}
        {hasMap && (
          <Section id="location" title="Location" icon={MapPin}>
            <div className="overflow-hidden rounded-2xl border border-border">
              <iframe
                title="Venue location"
                data-testid="venue-map"
                width="100%"
                height="360"
                loading="lazy"
                style={{ border: 0 }}
                src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5">
              <div>
                <p className="font-semibold">{event.venue_name}</p>
                <p className="text-sm text-muted-foreground">{event.address}</p>
              </div>
              <a href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`} target="_blank" rel="noreferrer" data-testid="directions-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
                <Navigation className="h-4 w-4" /> Get directions
              </a>
            </div>
          </Section>
        )}

        {/* Who's attending */}
        {attendees.enabled && (
          <Section id="attending" title="Who's attending" icon={UserCheck}>
            <p className="-mt-3 mb-5 text-center text-sm text-muted-foreground">
              <b className="text-foreground">{attendees.total}</b> {attendees.total === 1 ? "guest" : "guests"} celebrating so far
            </p>
            {attendees.names.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-2" data-testid="attendees-list">
                {attendees.names.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gold/15 text-xs font-bold text-gold">{a.name.charAt(0)}</span>
                    {a.name}{a.party_size > 1 && <span className="text-xs text-muted-foreground">+{a.party_size - 1}</span>}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-center text-sm text-muted-foreground">No RSVPs yet — be the first!</p>
            )}
          </Section>
        )}

        <div className="mt-16 text-center">
          <button data-testid="footer-rsvp-btn" onClick={() => setRsvpOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-base font-semibold text-primary-foreground transition-all hover:shadow-xl">
            RSVP to this event <ArrowRight className="h-5 w-5" />
          </button>
          <Link to="/" className="mt-8 block text-sm text-muted-foreground">Powered by Godinga</Link>
        </div>
      </div>

      {/* Lightbox */}
      <Lightbox items={lb.items} index={lb.index} onClose={() => setLb({ items: null, index: null })} onIndex={(i) => setLb((s) => ({ ...s, index: i }))} />

      {/* RSVP modal */}
      <AnimatePresence>
        {rsvpOpen && (
          <RsvpModal event={event} onClose={() => setRsvpOpen(false)} onDone={() => { loadAttendees(); }} />
        )}
      </AnimatePresence>
    </div>
  );
}

function Section({ id, title, icon: Icon, children }) {
  return (
    <section id={id} className="mt-16 scroll-mt-20">
      <div className="mb-8 flex flex-col items-center text-center">
        {Icon && <Icon className="h-6 w-6 text-gold" />}
        <h2 className="mt-2 font-serif text-3xl font-medium tracking-tight">{title}</h2>
        <span className="mt-3 h-px w-16 bg-gold/50" />
      </div>
      {children}
    </section>
  );
}

function RsvpModal({ event, onClose, onDone }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", status: "", party_size: 1,
    plus_one_names: [], meal_choice: "", plus_one_meals: [],
    custom_answers: {}, message: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const attending = form.status === "attending";
  const partyCount = Math.max(1, parseInt(form.party_size, 10) || 1);
  const plusCount = partyCount - 1;

  const steps = (() => {
    const s = ["identity", "response"];
    if (attending) {
      if (event.allow_plus_ones) s.push("party");
      if (event.meal_enabled && (event.meal_options || []).length) s.push("meals");
      if ((event.custom_questions || []).length) s.push("questions");
    }
    s.push("finish");
    return s;
  })();
  const current = steps[step];

  const canNext = () => {
    if (current === "identity") return form.name.trim() && form.email.trim();
    if (current === "response") return form.status !== "";
    return true;
  };
  const isSubmitStep = current === "finish" || (current === "response" && !attending);

  const next = () => {
    if (!canNext()) { toast.error("Please complete this step"); return; }
    if (isSubmitStep) { submit(); return; }
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        party_size: attending ? partyCount : 1,
        plus_one_names: attending ? form.plus_one_names.slice(0, plusCount).filter(Boolean) : [],
        plus_one_meals: attending ? form.plus_one_meals.slice(0, plusCount) : [],
        meal_choice: attending ? form.meal_choice : "",
      };
      await eventsApi.rsvp(event.id, payload);
      setDone(true);
      onDone();
    } catch {
      toast.error("Could not submit your RSVP. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[90] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-card p-6 shadow-2xl sm:rounded-3xl sm:p-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="rsvp-modal"
      >
        <button onClick={onClose} data-testid="rsvp-close" className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-muted-foreground hover:text-foreground">
          <X className="h-5 w-5" />
        </button>

        {done ? (
          <div className="py-4 text-center" data-testid="rsvp-confirmation">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-gold">
              {attending ? <Heart className="h-8 w-8" /> : <Sparkles className="h-8 w-8" />}
            </span>
            <h2 className="mt-6 font-serif text-3xl font-semibold">{attending ? "You're on the list!" : "Thank you"}</h2>
            <p className="mt-3 text-muted-foreground">
              {attending
                ? `We can't wait to celebrate with you${form.name ? `, ${form.name.split(" ")[0]}` : ""}.`
                : "You'll be missed dearly. Thank you for your reply."}
            </p>
            {attending && (
              <a href={buildIcs(event)} download={`${event.title}.ics`} data-testid="add-calendar-btn" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
                <CalendarPlus className="h-4 w-4" /> Add to calendar
              </a>
            )}
            <button onClick={onClose} className="mt-4 block w-full text-sm font-medium text-muted-foreground">Close</button>
          </div>
        ) : (
          <>
            <div className="mb-6 mt-2 flex items-center gap-1.5">
              {steps.map((s, i) => (
                <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-gold" : "bg-secondary"}`} />
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div key={current} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.22 }}>
                {current === "identity" && (
                  <div>
                    <h2 className="font-serif text-2xl font-semibold">Let's start with you</h2>
                    <p className="mt-1 text-sm text-muted-foreground">So the host knows who's replying.</p>
                    <div className="mt-6 space-y-4">
                      <input data-testid="rsvp-guest-name-input" className={inputCls} placeholder="Your full name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                      <input data-testid="rsvp-guest-email-input" type="email" className={inputCls} placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)} />
                    </div>
                  </div>
                )}

                {current === "response" && (
                  <div>
                    <h2 className="font-serif text-2xl font-semibold">Will you join us?</h2>
                    <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <button data-testid="rsvp-attending-yes-btn" onClick={() => set("status", "attending")} className={`flex flex-col items-center gap-2 rounded-2xl border-2 p-6 transition-all ${attending ? "border-gold bg-gold/10" : "border-border hover:border-gold/50"}`}>
                        <span className={`flex h-12 w-12 items-center justify-center rounded-full ${attending ? "bg-gold text-primary" : "bg-secondary"}`}><Check className="h-6 w-6" /></span>
                        <span className="font-semibold">Joyfully accept</span>
                      </button>
                      <button data-testid="rsvp-attending-no-btn" onClick={() => set("status", "declined")} className={`flex flex-col items-center gap-2 rounded-2xl border-2 p-6 transition-all ${form.status === "declined" ? "border-destructive bg-destructive/10" : "border-border hover:border-destructive/40"}`}>
                        <span className={`flex h-12 w-12 items-center justify-center rounded-full ${form.status === "declined" ? "bg-destructive text-white" : "bg-secondary"}`}><X className="h-6 w-6" /></span>
                        <span className="font-semibold">Regretfully decline</span>
                      </button>
                    </div>
                    <textarea className={`${inputCls} mt-4`} rows={2} placeholder="Leave a note for the host (optional)" value={form.message} onChange={(e) => set("message", e.target.value)} />
                  </div>
                )}

                {current === "party" && (
                  <div>
                    <h2 className="font-serif text-2xl font-semibold">Your party</h2>
                    <p className="mt-1 text-sm text-muted-foreground">Up to {event.max_party_size} total.</p>
                    <div className="mt-6 flex items-center gap-3">
                      {Array.from({ length: event.max_party_size }).map((_, i) => (
                        <button key={i} data-testid={`party-size-${i + 1}`} onClick={() => set("party_size", i + 1)} className={`flex h-11 w-11 items-center justify-center rounded-xl border-2 text-sm font-semibold transition-all ${partyCount === i + 1 ? "border-gold bg-gold/10 text-gold" : "border-border hover:border-gold/50"}`}>{i + 1}</button>
                      ))}
                    </div>
                    {plusCount > 0 && (
                      <div className="mt-6 space-y-3">
                        <span className="text-sm font-medium">Names of your guests</span>
                        {Array.from({ length: plusCount }).map((_, i) => (
                          <input key={i} data-testid={`rsvp-plusone-name-input-${i}`} className={inputCls} placeholder={`Guest ${i + 1} full name`} value={form.plus_one_names[i] || ""} onChange={(e) => { const arr = [...form.plus_one_names]; arr[i] = e.target.value; set("plus_one_names", arr); }} />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {current === "meals" && (
                  <div>
                    <h2 className="font-serif text-2xl font-semibold">Choose your meal</h2>
                    <MealPicker label="Your selection" testId="rsvp-meal-select" options={event.meal_options} value={form.meal_choice} onChange={(v) => set("meal_choice", v)} />
                    {plusCount > 0 && Array.from({ length: plusCount }).map((_, i) => (
                      <MealPicker key={i} label={`Meal for ${form.plus_one_names[i] || `Guest ${i + 1}`}`} testId={`rsvp-plusone-meal-${i}`} options={event.meal_options} value={form.plus_one_meals[i] || ""} onChange={(v) => { const arr = [...form.plus_one_meals]; arr[i] = v; set("plus_one_meals", arr); }} />
                    ))}
                  </div>
                )}

                {current === "questions" && (
                  <div>
                    <h2 className="font-serif text-2xl font-semibold">A few more things</h2>
                    <div className="mt-6 space-y-5">
                      {event.custom_questions.map((q) => (
                        <div key={q.id || q.label}>
                          <span className="text-sm font-medium">{q.label}{q.required && <span className="text-gold"> *</span>}</span>
                          {q.type === "yesno" ? (
                            <div className="mt-2 flex gap-3">
                              {["Yes", "No"].map((opt) => (
                                <button key={opt} data-testid={`question-${q.label}-${opt}`} onClick={() => set("custom_answers", { ...form.custom_answers, [q.label]: opt })} className={`rounded-xl border-2 px-6 py-2.5 text-sm font-semibold transition-all ${form.custom_answers[q.label] === opt ? "border-gold bg-gold/10 text-gold" : "border-border hover:border-gold/50"}`}>{opt}</button>
                              ))}
                            </div>
                          ) : q.type === "datetime" ? (
                            <input type="datetime-local" data-testid={`question-${q.label}-datetime`} className={`${inputCls} mt-2`} value={form.custom_answers[q.label] || ""} onChange={(e) => set("custom_answers", { ...form.custom_answers, [q.label]: e.target.value })} />
                          ) : q.type === "choice" ? (
                            <div className="mt-2 flex flex-wrap gap-3">
                              {(q.options || []).map((opt) => (
                                <button key={opt} data-testid={`question-${q.label}-${opt}`} onClick={() => set("custom_answers", { ...form.custom_answers, [q.label]: opt })} className={`rounded-xl border-2 px-6 py-2.5 text-sm font-semibold transition-all ${form.custom_answers[q.label] === opt ? "border-gold bg-gold/10 text-gold" : "border-border hover:border-gold/50"}`}>{opt}</button>
                              ))}
                            </div>
                          ) : q.type === "id_upload" ? (
                            <IdUploadField attendees={[form.name || "You", ...form.plus_one_names.slice(0, plusCount).map((n, idx) => n || `Guest ${idx + 1}`)]} value={form.custom_answers[q.label] || []} onChange={(v) => set("custom_answers", { ...form.custom_answers, [q.label]: v })} />
                          ) : (
                            <input className={`${inputCls} mt-2`} value={form.custom_answers[q.label] || ""} onChange={(e) => set("custom_answers", { ...form.custom_answers, [q.label]: e.target.value })} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {current === "finish" && (
                  <div className="text-center">
                    <Users className="mx-auto h-10 w-10 text-gold" />
                    <h2 className="mt-4 font-serif text-2xl font-semibold">Ready to send?</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {attending ? `${form.name}, confirm to lock in your spot${partyCount > 1 ? ` for a party of ${partyCount}` : ""}.` : "Confirm your response below."}
                    </p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="mt-8 flex items-center justify-between">
              <button onClick={back} disabled={step === 0} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-0">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button data-testid={isSubmitStep ? "rsvp-submit-btn" : "rsvp-next-btn"} onClick={next} disabled={submitting} className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition-all hover:shadow-lg disabled:opacity-60">
                {isSubmitStep ? (submitting ? "Sending..." : "Send RSVP") : "Continue"}
                {!isSubmitStep && <ArrowRight className="h-4 w-4" />}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

function IdUploadField({ attendees, value, onChange }) {
  const [busy, setBusy] = useState(-1);
  const entries = value || [];

  const handleFile = async (idx, name, file) => {
    if (!file) return;
    setBusy(idx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await eventsApi.upload(fd);
      const next = [...entries];
      next[idx] = { name, url: res.url, filename: res.filename };
      onChange(next);
      toast.success(`ID uploaded for ${name}`);
    } catch {
      toast.error("Upload failed. Try a smaller image or PDF.");
    } finally {
      setBusy(-1);
    }
  };

  return (
    <div className="mt-2 space-y-2" data-testid="id-upload-field">
      {attendees.map((name, idx) => {
        const entry = entries[idx];
        return (
          <label key={idx} data-testid={`id-upload-${idx}`} className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border-2 border-dashed px-4 py-3 transition-colors ${entry ? "border-gold/60 bg-gold/5" : "border-border hover:border-gold/50"}`}>
            <span className="flex items-center gap-3">
              {entry ? <FileCheck2 className="h-5 w-5 text-gold" /> : busy === idx ? <Loader2 className="h-5 w-5 animate-spin text-gold" /> : <UploadCloud className="h-5 w-5 text-muted-foreground" />}
              <span>
                <span className="block text-sm font-semibold">{name}</span>
                <span className="block text-xs text-muted-foreground">{entry ? entry.filename : "Upload govt. photo ID (image or PDF)"}</span>
              </span>
            </span>
            <span className="text-xs font-semibold text-gold">{entry ? "Replace" : "Choose file"}</span>
            <input type="file" accept="image/*,application/pdf" className="hidden" disabled={busy === idx} onChange={(e) => handleFile(idx, name, e.target.files[0])} />
          </label>
        );
      })}
    </div>
  );
}

function MealPicker({ label, options, value, onChange, testId }) {
  return (
    <div className="mt-5">
      <span className="text-sm font-medium">{label}</span>
      <div className="mt-2 space-y-2" data-testid={testId}>
        {options.map((m) => (
          <button key={m.id || m.label} onClick={() => onChange(m.label)} className={`flex w-full items-center justify-between rounded-xl border-2 px-4 py-3 text-left transition-all ${value === m.label ? "border-gold bg-gold/10" : "border-border hover:border-gold/50"}`}>
            <span>
              <span className="block text-sm font-semibold">{m.label}</span>
              {m.description && <span className="block text-xs text-muted-foreground">{m.description}</span>}
            </span>
            <span className="flex items-center gap-2">
              {m.dietary_tag && <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">{m.dietary_tag}</span>}
              {value === m.label && <Check className="h-4 w-4 text-gold" />}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
